/* Playoff banner slider — the championship final banner now rotates through the
   league's official transmissions. Auto-advances, pauses on hover/touch, arrows +
   dots. All slides are 16:9 landscape art filling the frame edge to edge. */
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const SLIDES = [
  {
    id: "championship-final",
    src: "/assets/riotball-playoffs-banner.webp",
    tag: "TEAM BATTLE — CHAMPIONSHIP FINAL",
    alt: "Dolo AL Riotball League Playoffs — the crimson champion holds the trophy and the glowing sphere over a field of fallen machines",
  },
  {
    id: "gameday-arena",
    src: "/assets/riotball-slide-gameday-arena.jpg",
    tag: "AL RIOTBALL GAMEDAY — INFINITE MULTIVERSE",
    alt: "AL Riotball Gameday — the red armored champion raises the charged sphere in the arena",
  },
  {
    id: "gameday-closeup",
    src: "/assets/riotball-slide-gameday-closeup.jpg",
    tag: "AL RIOTBALL GAMEDAY — CORE BEARER",
    alt: "Close-up of the red champion presenting the sparking AL Riotball core",
  },
  {
    id: "burning-ball",
    src: "/assets/riotball-slide-burning-ball.jpg",
    tag: "CHAMPIONSHIP PLAYOFFS — BURNING CIRCUIT",
    alt: "The AL Riotball in full flame tearing past the playoff mural as the crowd watches",
  },
];

const ROTATE_MS = 5200;

export function RiotballPlayoffSlider() {
  const [index, setIndex] = useState(0);
  const pausedRef = useRef(false);
  const slide = SLIDES[index];

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (!pausedRef.current) setIndex((i) => (i + 1) % SLIDES.length);
    }, ROTATE_MS);
    return () => window.clearInterval(timer);
  }, []);

  const go = (next) => setIndex(((next % SLIDES.length) + SLIDES.length) % SLIDES.length);

  return (
    <div
      className="playoff-slider"
      data-testid="riotball-playoff-slider"
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
      onTouchStart={() => { pausedRef.current = true; }}
      onTouchEnd={() => { window.setTimeout(() => { pausedRef.current = false; }, 2500); }}
    >
      <div className="playoff-slider-frame">
        <AnimatePresence mode="wait">
          <motion.div
            key={slide.id}
            className="playoff-slide"
            data-testid={`playoff-slide-${slide.id}`}
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.995 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <img className="playoff-slide-img" src={slide.src} alt={slide.alt} loading="lazy" />
            <span className="playoff-slide-tag" data-testid="playoff-slide-tag">{slide.tag}</span>
          </motion.div>
        </AnimatePresence>

        <button type="button" className="playoff-arrow is-prev" onClick={() => go(index - 1)} data-testid="playoff-slider-prev" aria-label="Previous banner">
          <ChevronLeft size={20} />
        </button>
        <button type="button" className="playoff-arrow is-next" onClick={() => go(index + 1)} data-testid="playoff-slider-next" aria-label="Next banner">
          <ChevronRight size={20} />
        </button>
      </div>

      <div className="playoff-dots" data-testid="playoff-slider-dots">
        {SLIDES.map((s, i) => (
          <button
            type="button"
            key={s.id}
            className={`playoff-dot${i === index ? " is-active" : ""}`}
            onClick={() => go(i)}
            data-testid={`playoff-slider-dot-${i}`}
            aria-label={`Show banner ${i + 1}: ${s.tag}`}
          />
        ))}
      </div>
    </div>
  );
}
